import { useRef, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import classes from './Bookings.module.css';
import BookedStudio from './BookedStudio';
import { getLastViewedStatus, hasNewBooking } from '../../util/bookingViewedTracker';
import useSearchParamsTabs from '../../hooks/useSearchParamsTabs';
import { buildCoarseLocationText } from '../../util/locationPrivacy';

/** Query values for ?buchungen=… (order: confirmed, requests, completed). Example: /creator?buchungen=anfragen */
export const CREATOR_BOOKINGS_TAB_IDS = Object.freeze([
    'bestaetigt',
    'anfragen',
    'abgeschlossen',
]);

/** @deprecated Use `CREATOR_BOOKINGS_TAB_IDS` (query param tabs, not hash). */
export const CREATOR_BOOKINGS_HASH_TAB_IDS = CREATOR_BOOKINGS_TAB_IDS;

const Bookings = ({ bookingData, isLoading, hasStudios, onTabChange }) => {
    const TAB_SWITCH_DELAY_MS = 420;
    const BOOKINGS_BATCH_SIZE = 10;
    const { user } = useSelector((state) => state?.auth);
    const userId = user?.id || user?._id;
    const excludedDebugStatuses = new Set(['EXPIRED', 'DECLINED', 'WITHDRAWN', 'CANCELLED']);
    
    const { activeIndex: activeTab, setTabIndex } = useSearchParamsTabs({
        paramName: 'buchungen',
        tabIds: CREATOR_BOOKINGS_TAB_IDS,
        defaultIndex: 0,
        onTabChange,
    });
    const [visualTab, setVisualTab] = useState(activeTab);
    const [indicatorStyle, setIndicatorStyle] = useState({});
    const listItemsRef = useRef([]);
    const navRef = useRef(null);
    const loadMoreRef = useRef(null);
    const tabSwitchTimeoutRef = useRef(null);
    const [visibleCountByTab, setVisibleCountByTab] = useState({
        0: BOOKINGS_BATCH_SIZE,
        1: BOOKINGS_BATCH_SIZE,
        2: BOOKINGS_BATCH_SIZE,
    });
    
    const organizeBookings = () => {
        if (!bookingData || !Array.isArray(bookingData)) return { confirmed: [], pending: [], completed: [], todayConfirmed: [] };

        const now = new Date();
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const confirmed = [];
        const pending = [];
        const completed = [];
        const todayConfirmed = [];

        bookingData.forEach(booking => {
            const bookingDateTime = new Date(booking.startDateTime);
            const bookingDate = new Date(booking.startDateTime);
            bookingDate.setHours(0, 0, 0, 0);
            
            if (booking.status === 'CANCELLED') {
                completed.push(booking);
                return;
            }
            
            // Check if booking has passed
            if (bookingDateTime <= now) {
                completed.push(booking);
            } else if (booking.status === 'PENDING') {
                pending.push(booking);
            } else if (booking.status === 'CONFIRMED') {
                // Check if confirmed booking is today
                if (bookingDate.getTime() === today.getTime()) {
                    todayConfirmed.push(booking);
                } else {
                    confirmed.push(booking);
                }
            }
        });

        return {
            confirmed: confirmed.sort((a, b) => new Date(a.startDateTime) - new Date(b.startDateTime)),
            pending: pending.sort((a, b) => new Date(a.startDateTime) - new Date(b.startDateTime)),
            completed: completed.sort((a, b) => new Date(b.startDateTime) - new Date(a.startDateTime)),
            todayConfirmed: todayConfirmed.sort((a, b) => new Date(a.startDateTime) - new Date(b.startDateTime))
        };
    };

    useEffect(() => {
        setVisualTab(activeTab);
    }, [activeTab]);

    useEffect(() => {
        return () => {
            if (tabSwitchTimeoutRef.current) {
                clearTimeout(tabSwitchTimeoutRef.current);
            }
        };
    }, []);

    const handleTabSwitch = (targetTab) => {
        setVisualTab(targetTab);

        if (tabSwitchTimeoutRef.current) {
            clearTimeout(tabSwitchTimeoutRef.current);
        }

        tabSwitchTimeoutRef.current = setTimeout(() => {
            setTabIndex(targetTab);
            tabSwitchTimeoutRef.current = null;
        }, TAB_SWITCH_DELAY_MS);
    };

    const { confirmed, pending, completed, todayConfirmed } = organizeBookings();
    
    // Split pending bookings into viewed and unviewed
    const { pendingViewed, pendingUnviewed } = useMemo(() => {
        if (!pending || pending.length === 0) return { pendingViewed: [], pendingUnviewed: [] };
        
        const viewed = [];
        const unviewed = [];
        
        pending.forEach(booking => {
            if (!booking?.id || !booking?.status) return;
            
            const lastViewed = getLastViewedStatus(booking.id, userId);
            // If never viewed or status changed since last view, it's unviewed
            if (!lastViewed || lastViewed.status !== booking.status) {
                unviewed.push(booking);
            } else {
                viewed.push(booking);
            }
        });
        
        return { pendingViewed: viewed, pendingUnviewed: unviewed };
    }, [pending, userId]);
    
    const getFilteredBookings = () => {
        switch(activeTab) {
            case 0:
                return [...todayConfirmed, ...confirmed];
            case 1:
                return pending;
            case 2:
                return completed;
            default:
                return [];
        }
    };

    const groupBookingsByDate = (bookings) => {
        if (activeTab === 0) {
            // For confirmed bookings, separate today and future
            return {
                today: todayConfirmed,
                future: confirmed
            };
        } else if (activeTab === 1) {
            // For pending requests, group by today and future
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            
            return bookings.reduce((acc, booking) => {
                const bookingDate = new Date(booking.startDateTime);
                bookingDate.setHours(0, 0, 0, 0);
                
                if (bookingDate.getTime() === today.getTime()) {
                    acc.today.push(booking);
                } else if (bookingDate > today) {
                    acc.future.push(booking);
                }
                
                return acc;
            }, { today: [], future: [] });
        } else {
            // For completed bookings, just return as future (no grouping needed)
            return {
                today: [],
                future: bookings
            };
        }
    };
    
    useEffect(() => {
        const activeItem = listItemsRef.current[visualTab];
        const navElement = navRef.current;
        
        if (activeItem && navElement) {
            const navRect = navElement.getBoundingClientRect();
            const itemRect = activeItem.getBoundingClientRect();
            
            setIndicatorStyle({
                left: `${itemRect.left - navRect.left}px`,
                width: `${itemRect.width}px`
            });
        }
    }, [visualTab]);

    const filteredBookings = getFilteredBookings();
    
    const groupedBookings = groupBookingsByDate(filteredBookings);
    const visibleCount = visibleCountByTab[activeTab] || BOOKINGS_BATCH_SIZE;

    const visibleGroupedBookings = useMemo(() => {
        if (activeTab === 0 || activeTab === 1) {
            return {
                ...groupedBookings,
                future: groupedBookings?.future?.slice(0, visibleCount) || [],
            };
        }

        return {
            ...groupedBookings,
            future: groupedBookings?.future?.slice(0, visibleCount) || [],
        };
    }, [activeTab, groupedBookings, visibleCount]);

    const hasMoreBookingsToLoad = useMemo(() => {
        const totalFutureBookings = groupedBookings?.future?.length || 0;
        return totalFutureBookings > visibleCount;
    }, [groupedBookings, visibleCount]);

    useEffect(() => {
        setVisibleCountByTab((prev) => ({
            ...prev,
            [activeTab]: BOOKINGS_BATCH_SIZE,
        }));
    }, [activeTab]);

    useEffect(() => {
        if (!hasMoreBookingsToLoad) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (!entries[0]?.isIntersecting) return;

                setVisibleCountByTab((prev) => ({
                    ...prev,
                    [activeTab]: (prev[activeTab] || BOOKINGS_BATCH_SIZE) + BOOKINGS_BATCH_SIZE,
                }));
            },
            {
                root: null,
                rootMargin: '250px',
                threshold: 0,
            }
        );

        const target = loadMoreRef.current;
        if (target) observer.observe(target);

        return () => {
            if (target) observer.unobserve(target);
            observer.disconnect();
        };
    }, [activeTab, hasMoreBookingsToLoad]);

    return (
        <div className={classes.bookingsContainer}>
            <h1 className={`${classes.titleDesktop} titleSemibold3`}>Buchungen</h1>
            <h1 className={`${classes.titleTablet} titleSemibold2`}>Buchungen</h1>
            <div className={classes.navigationContainer}>
                <nav ref={navRef} className={classes.navigationList}>
                    <li 
                        ref={el => listItemsRef.current[0] = el}
                        className={`semibold large ${visualTab === 0 ? classes.active : classes.inactive}`}
                        onClick={() => handleTabSwitch(0)}
                    >
                        Bestätigte Buchungen
                    </li>
                    <li 
                        ref={el => listItemsRef.current[1] = el}
                        className={`semibold large ${visualTab === 1 ? classes.active : classes.inactive}`}
                        onClick={() => handleTabSwitch(1)}
                    >
                       Anfragen
                       {pendingViewed?.length > 0 && (
                       <div  className={`${classes.badge} semiboldXxs`} style={{ backgroundColor: 'var(--grey-2)', marginRight: pendingUnviewed?.length > 0 ? '4px' : '0' }}>
                            {pendingViewed?.length}
                       </div>
                       )}
                       {pendingUnviewed?.length > 0 && (
                       <div  className={`${classes.badge} semiboldXxs`} style={{ backgroundColor: 'var(--stu-rot)' }}>
                            {pendingUnviewed?.length}
                       </div>
                       )}
                    </li>
                    <li 
                        ref={el => listItemsRef.current[2] = el}
                        className={`semibold large ${visualTab === 2 ? classes.active : classes.inactive}`}
                        onClick={() => handleTabSwitch(2)}
                    >
                        Abgeschlossen
                    </li>
                </nav>
                <nav className={classes.navigationLinksTablet}>
                    <li
                        onClick={() => handleTabSwitch(0)}
                        className={`semiboldLarge ${visualTab === 0 ? classes.activeTablet : classes.inactiveTablet}`}
                    >
                        Bestätigt
                    </li>
                    <li 
                        onClick={() => handleTabSwitch(1)}
                        className={`semiboldLarge ${visualTab === 1 ? classes.activeTablet : classes.inactiveTablet}`}
                    >
                       Anfragen
                       {pendingViewed?.length > 0 && (
                       <div  className={`${classes.badge} semiboldXxs`} style={{ backgroundColor: 'var(--grey-2)', marginRight: pendingUnviewed?.length > 0 ? '4px' : '0' }}>
                            {pendingViewed?.length}
                       </div>
                       )}
                       {pendingUnviewed?.length > 0 && (
                       <div  className={`${classes.badge} semiboldXxs`} style={{ backgroundColor: 'var(--stu-rot)' }}>
                            {pendingUnviewed?.length}
                       </div>
                       )}
                    </li>
                    <li 
                        onClick={() => handleTabSwitch(2)}
                        className={`semiboldLarge ${visualTab === 2 ? classes.activeTablet : classes.inactiveTablet}`}
                    >
                        Abgeschlossen
                    </li>
                </nav>
                <div className={classes.indicatorContainer}>
                    <div 
                        className={classes.indicator} 
                        style={indicatorStyle}
                    />
                    <div className={classes.horizontalLine}></div>
                </div>
            </div>
            {isLoading ? (
                <div className={classes.loadingContainer}>
                    <div className={classes.loadingSpinner}></div>
                </div>
            ) : filteredBookings?.length === 0 ? (
                <div className={classes.emptyContainer}>
                    <div className={classes.emptyStateContainer}>
                        <div className={classes.emptyStateBoxContainer}>
                            <div className={classes.emptyStateBox} />
                            <div className={classes.emptyStateBox} />
                            <div className={classes.emptyStateBox} />
                            <div className={classes.emptyStateBoxTablet} />
                        </div>
                        <div className={classes.emptyStateContent}>
                            <h2 className="titleSemibold">
                                {hasStudios ? (
                                    "Noch keine Buchungen"
                                ) : (
                                    "Erstelle dein Studio"
                                )}
                            </h2>
                            <p className="mediumLarge">
                                {hasStudios ? (
                                    "Verbessere dein Studio und werde gebucht"
                                ) : (
                                    "Erstelle dein Studio in wenigen Klicks und werde gebucht"
                                )}
                            </p>
                            {!hasStudios ? (
                                <Link style={{ color: 'white' }} to="/studio-erstellen" className={`${classes.improveButton} regularSmall`}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="17" viewBox="0 0 18 17" fill="none">
                                        <path fillRule="evenodd" clipRule="evenodd" d="M17 9.0317H1V7.95539H17V9.0317Z" fill="white"/>
                                        <path fillRule="evenodd" clipRule="evenodd" d="M9.53433 0.5V16.5H8.45801V0.5H9.53433Z" fill="white"/>
                                        <path fillRule="evenodd" clipRule="evenodd" d="M17 9.0317H1V7.95539H17V9.0317Z" stroke="white" strokeWidth="0.2" strokeMiterlimit="10"/>
                                        <path fillRule="evenodd" clipRule="evenodd" d="M9.53433 0.5V16.5H8.45801V0.5H9.53433Z" stroke="white" strokeWidth="0.2" strokeMiterlimit="10"/>
                                    </svg>
                                    Studio erstellen
                                </Link>
                                ) : (
                                <Link style={{ color: 'white' }} to="/services-studios" className={`${classes.improveButton} regularSmall`}>
                                    Studio verbessern
                                </Link>
                                )}
                        </div>
                    </div>
                </div>
            ) : (
            <div className={classes.bookingsContent}>
                {activeTab === 0 && (
                    <>
                        {visibleGroupedBookings?.today?.length > 0 && (
                            <div className={classes.bookings}>
                                <h2 className="semiboldLarge" style={{ color: 'var(--stu-rot)' }}>Heute</h2>
                                <div className={classes.todayContent}>
                                    {visibleGroupedBookings?.today?.map((booking, index) => {
                                        const hasNotification = hasNewBooking(booking, userId);
                                        return (
                                            <div 
                                                className={classes.bookingWrapper}
                                                key={`${booking.id}-${index}`}
                                                style={hasNotification ? {
                                                    border: '2.5px solid var(--stu-rot)',
                                                    borderRadius: '20px',
                                                    padding: '0'
                                                } : {}}
                                            >
                                                <BookedStudio 
                                                    hasNotification={hasNotification}
                                                    studioData={{
                                                        Studio: {
                                                            id: booking.Studio.id,
                                                            StudioName: booking.Studio.StudioName,
                                                            processedImages: booking.Studio.processedImages,
                                                            studioLocation: buildCoarseLocationText(booking.Studio)
                                                        },
                                                        booking: {
                                                            id: booking.id,
                                                            startDateTime: booking.startDateTime,
                                                            endDateTime: booking.endDateTime,
                                                            totalPrice: booking.totalPrice,
                                                            status: booking.status,
                                                            cancelledBy: booking.cancelledBy,
                                                            projectDescription: booking.projectDescription,
                                                            createdAt: booking.createdAt,
                                                            User: booking.User
                                                        },
                                                        creativePackages: booking.creativePackages || []
                                                    }}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        {visibleGroupedBookings?.future?.length > 0 ? (
                            <div className={classes.bookings}>
                                <h2 className="semiboldLarge">Nächste Buchungen</h2>
                                <div className={classes.todayContent}>
                                    {visibleGroupedBookings?.future?.map((booking, index) => {
                                        const hasNotification = hasNewBooking(booking, userId);
                                        return (
                                            <div 
                                                key={`${booking.id}-${index}`}
                                                style={hasNotification ? {
                                                    border: '2.5px solid var(--stu-rot)',
                                                    borderRadius: '20px',
                                                    padding: '0'
                                                } : {}}
                                                className={classes.bookingWrapper}
                                            >
                                                <BookedStudio 
                                                    hasNotification={hasNotification}
                                                    studioData={{
                                                        Studio: {
                                                            id: booking.Studio.id,
                                                            StudioName: booking.Studio.StudioName,
                                                            processedImages: booking.Studio.processedImages,
                                                            studioLocation: buildCoarseLocationText(booking.Studio)
                                                        },
                                                        booking: {
                                                            id: booking.id,
                                                            startDateTime: booking.startDateTime,
                                                            endDateTime: booking.endDateTime,
                                                            totalPrice: booking.totalPrice,
                                                            status: booking.status,
                                                            cancelledBy: booking.cancelledBy,
                                                            projectDescription: booking.projectDescription,
                                                            createdAt: booking.createdAt,
                                                            User: booking.User
                                                        },
                                                        creativePackages: booking.creativePackages || []
                                                    }}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : (
                            visibleGroupedBookings?.today?.length === 0 && (
                                <div className={classes.bookings}>
                                    <h2 className="semiboldLarge">Nächste Buchungen</h2>
                                    <div className={classes.todayContent}>
                                        <p>Keine bestätigten Buchungen</p>
                                    </div>
                                </div>
                            )
                        )}
                    </>
                )}
                
                {activeTab === 1 && (
                    <>
                        {visibleGroupedBookings.today.length > 0 && (
                            <div className={classes.bookings}>
                                <h2 className="semiboldLarge" style={{ color: 'var(--stu-rot)' }}>Heute</h2>
                                <div className={classes.todayContent}>
                                    {visibleGroupedBookings.today.map((booking, index) => {
                                        const hasNotification = hasNewBooking(booking, userId);
                                        return (
                                            <div 
                                                className={classes.bookingWrapper}
                                                key={`${booking.id}-${index}`}
                                                style={hasNotification ? {
                                                    border: '2.5px solid var(--stu-rot)',
                                                    borderRadius: '20px',
                                                    padding: '0'
                                                } : {}}
                                            >
                                                <BookedStudio 
                                                    hasNotification={hasNotification}
                                                    studioData={{
                                                        Studio: {
                                                            id: booking.Studio.id,
                                                            StudioName: booking.Studio.StudioName,
                                                            processedImages: booking.Studio.processedImages,
                                                            studioLocation: buildCoarseLocationText(booking.Studio)
                                                        },
                                                        booking: {
                                                            id: booking.id,
                                                            startDateTime: booking.startDateTime,
                                                            endDateTime: booking.endDateTime,
                                                            totalPrice: booking.totalPrice,
                                                            status: booking.status,
                                                            cancelledBy: booking.cancelledBy,
                                                            projectDescription: booking.projectDescription,
                                                            createdAt: booking.createdAt,
                                                            User: booking.User
                                                        },
                                                        creativePackages: booking.creativePackages || []
                                                    }}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                        {visibleGroupedBookings?.future?.length > 0 ? (
                            <div className={classes.bookings}>
                                <h2 className="semiboldLarge">Anfragen</h2>
                                <div className={classes.todayContent}>
                                    {visibleGroupedBookings?.future?.map((booking, index) => {
                                        const hasNotification = hasNewBooking(booking, userId);
                                        return (
                                            <div 
                                                className={classes.bookingWrapper}
                                                key={`${booking.id}-${index}`}
                                                style={hasNotification ? {
                                                    border: '2.5px solid var(--stu-rot)',
                                                    borderRadius: '20px',
                                                    padding: '0'
                                                } : {}}
                                            >
                                                <BookedStudio 
                                                    studioData={{   
                                                        Studio: {
                                                            id: booking.Studio.id,
                                                            StudioName: booking.Studio.StudioName,
                                                            processedImages: booking.Studio.processedImages,
                                                            studioLocation: buildCoarseLocationText(booking.Studio)
                                                        },
                                                        booking: {
                                                            id: booking.id,
                                                            startDateTime: booking.startDateTime,
                                                            endDateTime: booking.endDateTime,
                                                            totalPrice: booking.totalPrice,
                                                            status: booking.status,
                                                            cancelledBy: booking.cancelledBy,
                                                            projectDescription: booking.projectDescription,
                                                            createdAt: booking.createdAt,
                                                            User: booking.User
                                                        },
                                                        creativePackages: booking.creativePackages || []
                                                    }}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ) : (
                            visibleGroupedBookings.today.length === 0 && (
                                <div className={classes.bookings}>
                                    <h2 className="semiboldLarge">Anfragen</h2>
                                    <div className={classes.todayContent}>
                                        <p>Keine Anfragen</p>
                                    </div>
                                </div>
                            )
                        )}
                    </>
                )}
                
                {activeTab === 2 && (
                    <div className={classes.bookings}>
                        <h2 className="semiboldLarge">Abgeschlossene Buchungen</h2>
                        <div className={classes.todayContent}>
                            {visibleGroupedBookings?.future?.length > 0 ? (
                                visibleGroupedBookings?.future?.map((booking, index) => {
                                    const hasNotification = hasNewBooking(booking, userId);
                                    return (
                                        <div 
                                            className={classes.bookingWrapper}
                                            key={`${booking.id}-${index}`}
                                            style={hasNotification ? {
                                                border: '2.5px solid var(--stu-rot)',
                                                borderRadius: '20px',
                                                padding: '0'
                                            } : {}}
                                        >
                                            <BookedStudio 
                                                hasNotification={hasNotification}
                                                studioData={{
                                                    Studio: {
                                                        id: booking.Studio.id,
                                                        StudioName: booking.Studio.StudioName,
                                                        processedImages: booking.Studio.processedImages,
                                                        studioLocation: buildCoarseLocationText(booking.Studio)
                                                    },
                                                    booking: {
                                                        id: booking.id,
                                                        startDateTime: booking.startDateTime,
                                                        endDateTime: booking.endDateTime,
                                                        totalPrice: booking.totalPrice,
                                                        status: booking.status,
                                                        projectDescription: booking.projectDescription,
                                                        createdAt: booking.createdAt,
                                                        User: booking.User
                                                    },
                                                    creativePackages: booking.creativePackages || []
                                                }}
                                            />
                                        </div>
                                    );
                                })
                            ) : (
                                <p>Keine abgeschlossenen Buchungen</p>
                            )}
                        </div>
                    </div>
                )}
                {hasMoreBookingsToLoad && (
                    <div
                        ref={loadMoreRef}
                        style={{ height: '1px', width: '100%' }}
                        aria-hidden="true"
                    />
                )}
            </div>
            )}
        </div>
    );
};


export default Bookings;
